type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

/** Forecasts & targets screen (`fin.fc`). */
export const fcPl = {
  fc: {
    subtitle: 'Czy zrobimy plan w tym miesiącu? Prognoza, szansa na cel i tempo, którego potrzeba.',
    asOf: 'Stan na',
    probability: 'Szansa na cel',
    target: 'Cel',
    sales: 'Sprzedaż',
    forecast: 'Prognoza',
    interval: 'Przedział 80%',
    intervalHint: 'Prognoza z marginesem błędu',
    realization: '{value} celu',
    forecastPct: '{value} celu',
    focusRep: 'Pokaż tylko tego handlowca',
    workdays: 'Dzień roboczy {done}/{total} · zostało {left}',
    workdaysTitle: 'Dni robocze miesiąca',
    seasonality: 'Sezonowość {value}×',
    scope: { OWN: 'mój wynik', TEAM: 'zespół', ALL: 'cała firma' },
    status: { ACHIEVED: 'Cel osiągnięty', ON_TRACK: 'Na dobrej drodze', AT_RISK: 'Zagrożony', OFF_TRACK: 'Poza planem', NO_TARGET: 'Brak celu' },
    scenario: { pessimistic: 'Pesymistyczny', base: 'Bazowy', optimistic: 'Optymistyczny' },
    hero: {
      title: '{month} · {scope}',
      subtitle: 'Dzień roboczy {done} z {total}, zostało {left}.',
      explain: 'Szansa = prawdopodobieństwo, że sprzedaż na koniec miesiąca dobije do celu. Kreski na łuku: 35% i 70%.',
      noTarget: 'Brak ustawionego celu — nie liczymy szansy na jego osiągnięcie.',
    },
    pace: {
      title: 'Tempo dzienne',
      required: 'Wymagane',
      runRate: 'Obecne (MTD)',
      expected: 'Oczekiwane',
      noTarget: 'Bez celu nie liczymy wymaganego tempa.',
      done: 'Cel już osiągnięty — dalsza sprzedaż to nadwyżka.',
      behind: 'Brakuje {value} dziennie do wymaganego tempa.',
      ahead: 'Obecne tempo wystarcza do celu.',
    },
    reps: { title: 'Prognoza wg handlowca' },
    chart: {
      title: 'Prognoza na 3 miesiące',
      subtitle: '24 miesiące historii, prognoza z przedziałem 80% i celami; niżej wskaźnik sezonowości.',
      actual: 'Sprzedaż',
      band: 'Przedział 80% (wariant bazowy)',
      seasonality: 'Sezonowość',
      note: 'Czynnik trendu: {trend} (ostatnie 12 mies. vs poprzednie 12) · typowy błąd prognozy sezonowej: {error}.',
    },
    history: {
      title: 'Cel a wykonanie',
      subtitle: 'Ostatnie miesiące: ilu handlowców zrobiło plan.',
      month: 'Miesiąc',
      realization: 'Realizacja celu',
      marginTarget: 'Cel marży',
      reps: 'Plan zrobiło',
    },
    repHistory: {
      title: 'Realizacja wg handlowca',
      subtitle: 'Procent celu miesiąca, ostatnie 6 miesięcy.',
      noTarget: 'Brak celu w tym miesiącu',
      legendGood: '≥ 100%',
      legendWarn: '85–99%',
      legendBad: '< 85%',
    },
    method: { title: 'Jak liczymy prognozę?' },
  },
};

export const fcEn: Shape<typeof fcPl> = {
  fc: {
    subtitle: 'Will we hit the plan this month? Forecast, chance of reaching the target and the pace needed.',
    asOf: 'As of',
    probability: 'Chance to hit target',
    target: 'Target',
    sales: 'Sales',
    forecast: 'Forecast',
    interval: '80% interval',
    intervalHint: 'Forecast with error margin',
    realization: '{value} of target',
    forecastPct: '{value} of target',
    focusRep: 'Show only this rep',
    workdays: 'Working day {done}/{total} · {left} left',
    workdaysTitle: 'Working days of the month',
    seasonality: 'Seasonality {value}×',
    scope: { OWN: 'my result', TEAM: 'team', ALL: 'whole company' },
    status: { ACHIEVED: 'Target achieved', ON_TRACK: 'On track', AT_RISK: 'At risk', OFF_TRACK: 'Off plan', NO_TARGET: 'No target' },
    scenario: { pessimistic: 'Pessimistic', base: 'Base', optimistic: 'Optimistic' },
    hero: {
      title: '{month} · {scope}',
      subtitle: 'Working day {done} of {total}, {left} left.',
      explain: 'Chance = probability that end-of-month sales reach the target. Ticks on the arc: 35% and 70%.',
      noTarget: 'No target set — the chance of reaching it is not calculated.',
    },
    pace: {
      title: 'Daily pace',
      required: 'Required',
      runRate: 'Current (MTD)',
      expected: 'Expected',
      noTarget: 'Without a target the required pace is not calculated.',
      done: 'Target already achieved — further sales are surplus.',
      behind: '{value} per day short of the required pace.',
      ahead: 'The current pace is enough to reach the target.',
    },
    reps: { title: 'Forecast by rep' },
    chart: {
      title: '3-month forecast',
      subtitle: '24 months of history, forecast with an 80% interval and targets; seasonality index below.',
      actual: 'Sales',
      band: '80% interval (base scenario)',
      seasonality: 'Seasonality',
      note: 'Trend factor: {trend} (last 12 months vs the 12 before) · typical seasonal forecast error: {error}.',
    },
    history: {
      title: 'Target vs actual',
      subtitle: 'Recent months: how many reps made their plan.',
      month: 'Month',
      realization: 'Target attainment',
      marginTarget: 'Margin target',
      reps: 'Made plan',
    },
    repHistory: {
      title: 'Attainment by rep',
      subtitle: 'Percent of the monthly target, last 6 months.',
      noTarget: 'No target this month',
      legendGood: '≥ 100%',
      legendWarn: '85–99%',
      legendBad: '< 85%',
    },
    method: { title: 'How is the forecast calculated?' },
  },
};
